import { ScrollShadow, ScrollShadowVisibility } from '@heroui/react';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { NextButton, PrevButton, usePrevNextButtons } from './buttons';

type Props<T> = {
  slides: T[];
  options?: EmblaOptionsType;
  children: (slide: T) => ReactNode;
  arrowStyle?: React.CSSProperties;
  arrowSpacing?: 'sm' | 'md';
};

const Carousel = <T,>({ slides, options, children, arrowStyle, arrowSpacing = 'md' }: Props<T>) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);

  const [visibility, setVisibility] = useState<ScrollShadowVisibility | null>(null);

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi);

  const logSlidesInView = useCallback((api: typeof emblaApi) => {
    if (!api) return;
    const value = api.slidesInView();
    if (value[0] === 0) {
      setVisibility('right');
    } else if (value[value.length - 1] === api.slideNodes().length - 1) {
      setVisibility('left');
    } else {
      setVisibility('both');
    }
  }, []);

  useEffect(() => {
    if (emblaApi) emblaApi.on('slidesInView', logSlidesInView);
  }, [emblaApi, logSlidesInView]);

  return (
    <section
      className={`relative m-0 px-0 @md:m-auto ${arrowSpacing === 'sm' ? '@md:px-4' : '@md:px-[50px]'}`}
    >
      <PrevButton
        onClick={onPrevButtonClick}
        disabled={prevBtnDisabled}
        className={`top-1/2 left-0 hidden -translate-y-1/2 @md:absolute @md:flex`}
        style={arrowStyle}
      />
      <ScrollShadow
        orientation="horizontal"
        visibility={visibility!}
        className="overflow-hidden py-4"
        ref={emblaRef}
      >
        <div className="ml-[calc(1rem * -1)] mx-6 flex touch-pan-y gap-4">
          {slides.map(children)}
        </div>
      </ScrollShadow>
      <NextButton
        onClick={onNextButtonClick}
        disabled={nextBtnDisabled}
        className={`top-1/2 right-0 hidden -translate-y-1/2 @md:absolute @md:flex`}
        style={arrowStyle}
      />
    </section>
  );
};

export default Carousel;
